import type { NormalizedColumnEntity, WebLegacyWidgetEntity } from '@web/types';
import type { Action } from '@ngrx/store';

import { Injectable, inject } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { switchMap } from 'rxjs/operators';

import { EHorizontalPosition } from '@common/enums';

import { columnSchema, webNormalize } from '@web/store/page-structure/page-structure/mockStructureData';

import { ColumnsFacade } from '@web/store/page-structure/columns/columns.facade';
import { PageStructureService } from '@web/store/page-structure/page-structure/page-structure.service';

import { isLegacyWidget } from '@common/helpers/widget-envelope';
import { WebWidgetsFactoryService } from '@web/core/services/web-widgets-factory.service';

import ColumnsActions from './columns.actions';
import GridsActions from '@web/store/page-structure/grids/grids.actions';
import WidgetsLegacyActions from '../widgets/widgets.actions';
import TabsActions from '../tabs/tabs.actions';

@Injectable()
export class ColumnsEffects {
    private readonly actions$ = inject(Actions);
    private readonly columnsFacade = inject(ColumnsFacade);

    public readonly createColumn$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ColumnsActions.createColumn),
            switchMap(({ sectionId, index, widgetCorrelationData, skipHistory }) => {
                const newColumnInstance = this.columnsFacade.getColumnInstanceValue(sectionId);
                const actions: Action[] = [
                    ColumnsActions.columnCreated({ column: newColumnInstance, index, skipHistory }),
                ];

                if (widgetCorrelationData?.widget)
                    actions.push(
                        WidgetsLegacyActions.createWidget({
                            columnId: newColumnInstance.id,
                            index: 0,
                            widget: widgetCorrelationData.widget,
                            skipHistory,
                        })
                    );
                return actions;
            })
        )
    );

    public readonly createSpecificColumn$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ColumnsActions.createSpecificColumn),
            switchMap(({ sectionId, index, skipHistory }) => {
                // The three header slots differ only in where they pull their
                // widgets: the left slot pushes them right, and vice versa.
                const leftColumnInstance = this.columnsFacade.getSpecificColumnInstanceValue(sectionId);
                leftColumnInstance.props.horizontalAlign = 'right';
                const centralColumnInstance = this.columnsFacade.getSpecificColumnInstanceValue(sectionId);
                centralColumnInstance.props.horizontalAlign = 'center';
                const rightColumnInstance = this.columnsFacade.getSpecificColumnInstanceValue(sectionId);
                rightColumnInstance.props.horizontalAlign = 'left';
                const actions: Action[] = [
                    ColumnsActions.columnCreated({ column: leftColumnInstance, index, skipHistory }),
                    ColumnsActions.columnCreated({ column: centralColumnInstance, index, skipHistory }),
                    ColumnsActions.columnCreated({ column: rightColumnInstance, index, skipHistory }),
                ];

                return actions;
            })
        )
    );

    public readonly copyColumn$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ColumnsActions.copyColumn),
            switchMap(({ column, index }) => {
                const copiedColumnEntity = WebWidgetsFactoryService.recursiveEntityCopy(column);
                const { columns, widgets: copiedWidgets } = webNormalize(copiedColumnEntity, columnSchema);
                const copiedColumn = columns[0];

                const { widgets, grids, tabs } = copiedWidgets.reduce(
                    ({ widgets, grids, tabs }, item) => {
                        if (PageStructureService.isEntityTab(item)) {
                            tabs.push(item);
                        } else if (PageStructureService.isEntityGrid(item)) {
                            grids.push(item);
                        } else {
                            widgets.push(item);
                        }
                        return { widgets, grids, tabs };
                    },
                    { widgets: [], grids: [], tabs: [] }
                );

                return [
                    ColumnsActions.columnCopied({
                        column: copiedColumn,
                        columnEntity: copiedColumnEntity,
                        index: index + 1,
                    }),
                    TabsActions.addMany({ entities: tabs }),
                    GridsActions.copyMany({
                        entities: grids.map((item) => {
                            const copy = {
                                ...item,
                                columnId: copiedColumn.id,
                            };
                            // Grid parent is resolved via parentId (tab when nested, otherwise the column).
                            const tab = tabs.find((tab) => tab.children.includes(item.id));
                            copy['parentId'] = tab ? tab.id : copiedColumn.id;

                            return copy;
                        }),
                    }),
                    WidgetsLegacyActions.copyManyWidgets({
                        widgets: widgets.map((item) => {
                            const copy = { ...item };
                            const grid = grids.find((grid) => grid.children.some((row) => row.includes(item.id)));
                            if (grid) {
                                // Legacy keeps the grid in `structureId`; otherwise it is the widget's single parent link.
                                copy[isLegacyWidget(item) ? 'structureId' : 'parentId'] = grid.id;
                            }

                            return copy;
                        }),
                    }),
                ];
            })
        )
    );

    public readonly addColumn$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ColumnsActions.addColumn),
            switchMap(({ column, index }) => [
                ColumnsActions.columnAdded({
                    column: {
                        ...column,
                        children: column.children.map((widget: WebLegacyWidgetEntity) => ({
                            id: widget.id,
                            schema: 'widget',
                        })),
                    } as NormalizedColumnEntity,
                    index,
                }),
                WidgetsLegacyActions.addManyWidgets({ widgets: column.children }),
            ])
        )
    );
}
